import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  applyDesign,
  buildDesignExtras,
  designManifest,
  normalizeDesignExtras,
  type DesignAsset,
  type DesignManifestEntry,
  type DesignedProps,
} from '@sarnia/shared';
import { editorStore, useEditorSelector } from '../editorStore';
import { api } from '../../lib/api';
import PublishDialog from '../PublishDialog';
import { Icon, type IconName } from '../../components/ui/Icon';
import { ThemeToggle } from '../../components/ui/ThemeToggle';
import './designed.css';
import './panels/formPanels.css';
import ContentPanel from './panels/ContentPanel';
import ColoursPanel from './panels/ColoursPanel';
import ImagesPanel from './panels/ImagesPanel';
import LinksPanel from './panels/LinksPanel';
import SectionsPanel from './panels/SectionsPanel';
import EffectsPanel from './panels/EffectsPanel';
import FontsPanel from './panels/FontsPanel';
import ItemsPanel from './panels/ItemsPanel';
import CheckPanel, { CheckSummary } from './panels/CheckPanel';
import BusinessPanel from './panels/BusinessPanel';
import { defaultTexts, runSiteCheck } from './siteCheck';
import { useAssetDataUrls } from './useAssetDataUrls';
import type { PanelCtx, PreviewSelection } from './panelTypes';

type TabId = 'content' | 'items' | 'images' | 'links' | 'business' | 'sections' | 'effects' | 'colours' | 'fonts' | 'check' | 'publish';
type PanelProps = Required<Pick<DesignedProps, 'images' | 'links' | 'hidden' | 'order'>> & DesignedProps;

const TABS: Array<{ id: TabId; label: string; icon: IconName }> = [
  { id: 'content', label: 'Content', icon: 'type' },
  { id: 'items', label: 'Items', icon: 'grid' },
  { id: 'images', label: 'Images', icon: 'image' },
  { id: 'links', label: 'Links', icon: 'link' },
  { id: 'business', label: 'Business', icon: 'globe' },
  { id: 'sections', label: 'Sections', icon: 'layout' },
  { id: 'effects', label: 'Effects', icon: 'sparkles' },
  { id: 'colours', label: 'Colours', icon: 'palette' },
  { id: 'fonts', label: 'Fonts', icon: 'book' },
  { id: 'check', label: 'Check', icon: 'shield' },
  { id: 'publish', label: 'Share', icon: 'rocket' },
];

const fallbackPanelProps: PanelProps = {
  designId: '',
  texts: {},
  vars: {},
  images: {},
  links: {},
  hidden: [],
  order: [],
};

function normalizeDesignedProps(
  props: DesignedProps,
  entry: DesignManifestEntry | undefined,
): PanelProps {
  const extras = normalizeDesignExtras(
    props as unknown as Record<string, unknown>,
    entry?.sections.length ?? 0,
  );
  return {
    ...props,
    images: extras.value.images,
    links: extras.value.links,
    hidden: extras.value.hidden,
    order: extras.value.order,
    fx: extras.value.fx ?? props.fx,
    fonts: extras.value.fonts ?? props.fonts,
  };
}

/** Current list items (edited or original) per data list, so the preview can match a clicked card to its item. */
function itemsForPreview(
  entry: DesignManifestEntry | undefined,
  props: DesignedProps | undefined
): Record<string, Array<Record<string, unknown>>> {
  const out: Record<string, Array<Record<string, unknown>>> = {};
  for (const d of entry?.data ?? []) {
    if (d.kind !== 'array') continue;
    const override = props?.data?.[d.name];
    out[d.name] = (Array.isArray(override) ? override : d.items) as Array<Record<string, unknown>>;
  }
  return out;
}

export default function DesignedWorkspace() {
  const [designHtml, setDesignHtml] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [previewSrcDoc, setPreviewSrcDoc] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [activeTab, setActiveTab] = useState<TabId>('content');
  const [selection, setSelection] = useState<PreviewSelection | null>(null);
  // The loading screen disappears seconds after the page loads, so it gets its own view where it stays on screen.
  const [loaderView, setLoaderView] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const lastWrittenPropsRef = useRef<string | null>(null);
  const lastRenderedMetaRef = useRef<string | null>(null);
  // Where the preview was scrolled to, so a reload (item edits, undo/redo) does not throw the user back to the top.
  const scrollYRef = useRef(0);

  const block = useEditorSelector((state) => state.site?.blocks[0]);
  const meta = useEditorSelector((state) => state.site?.meta);
  const previewDevice = useEditorSelector((state) => state.previewDevice);
  const assets = useEditorSelector((state) => state.assets);

  const designId = block?.type === 'designed' ? (block.props as DesignedProps).designId : undefined;
  const rawProps = block?.type === 'designed' ? (block.props as DesignedProps) : undefined;

  const entry = useMemo(
    () => designManifest.find((d) => d.id === designId),
    [designId],
  );

  const props = useMemo(
    () => (rawProps ? normalizeDesignedProps(rawProps, entry) : rawProps),
    [rawProps, entry],
  );

  const referencedAssetIds = useMemo(() => {
    const ids = new Set<string>();
    for (const img of Object.values(props?.images ?? {})) if (img?.assetId) ids.add(img.assetId);
    // Pictures attached to list items (products, books, ...) live inside the data lists as a `photo` asset id.
    for (const list of Object.values(props?.data ?? {})) {
      if (!Array.isArray(list)) continue;
      for (const item of list) {
        const photo = item && typeof item === 'object' ? (item as Record<string, unknown>)['photo'] : undefined;
        if (typeof photo === 'string' && photo) ids.add(photo);
      }
    }
    return Array.from(ids);
  }, [props]);

  const allAssetIds = useMemo(() => Object.keys(assets), [assets]);

  const { urls: referencedUrls, ready: referencedReady } = useAssetDataUrls(assets, referencedAssetIds);
  const { urls: allAssetUrls } = useAssetDataUrls(assets, allAssetIds);

  useEffect(() => {
    if (!designId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    api.get<{ id: string; html: string }>(`/designs/${designId}`)
      .then((res) => {
        if (!cancelled) {
          setDesignHtml(res.html);
          setLoading(false);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load design');
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [designId]);

  // Latest values for the (re)build effect, read through refs so editing never retriggers it.
  const latestRef = useRef({ props, meta, assets, referencedAssetIds, referencedUrls, entry });
  latestRef.current = { props, meta, assets, referencedAssetIds, referencedUrls, entry };

  // Build the preview document: when the design html arrives, when its images have resolved, or when `reloadKey` is bumped
  // because the block props changed from outside the iframe (undo / redo). Never on ordinary edits.
  useEffect(() => {
    if (!designHtml || !referencedReady) return;
    const cur = latestRef.current;
    if (!cur.props || !cur.meta) return;
    const assetsMap: Record<string, DesignAsset> = {};
    for (const assetId of cur.referencedAssetIds) {
      const url = cur.referencedUrls[assetId];
      if (url) assetsMap[assetId] = { url, alt: cur.assets[assetId]?.alt ?? '' };
    }
    setPreviewSrcDoc(
      applyDesign(designHtml, cur.props, cur.meta, {
        editor: true,
        assets: assetsMap,
        sectionCount: cur.entry?.sections.length ?? 0,
        items: itemsForPreview(cur.entry, cur.props),
        extraHtml: buildDesignExtras(cur.props, {}),
        view: loaderView ? 'loader' : 'page'
      })
    );
    lastRenderedMetaRef.current = JSON.stringify(cur.meta);
  }, [designHtml, referencedReady, reloadKey, loaderView]);

  // Detect changes that did not come from this component (undo / redo) and rebuild the preview for them.
  const rawPropsJson = rawProps ? JSON.stringify(rawProps) : null;
  useEffect(() => {
    if (rawPropsJson === null) return;
    if (lastWrittenPropsRef.current === null) {
      lastWrittenPropsRef.current = rawPropsJson;
      return;
    }
    if (rawPropsJson !== lastWrittenPropsRef.current) {
      lastWrittenPropsRef.current = rawPropsJson;
      setReloadKey((k) => k + 1);
    }
  }, [rawPropsJson]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      const iframe = iframeRef.current;
      if (!iframe || event.source !== iframe.contentWindow) return;
      const data = event.data as {
        source?: string;
        type?: string;
        id?: string;
        text?: string;
        y?: number;
        name?: string;
        index?: number;
      };
      if (!data || data.source !== 'sarnia-design') return;

      if (data.type === 'text') {
        if (typeof data.id !== 'string' || !/^\d{1,4}$/.test(data.id)) return;
        if (typeof data.text !== 'string' || data.text.length > 2000) return;
        const state = editorStore.getState();
        const currentBlock = state.site?.blocks[0];
        if (!currentBlock || currentBlock.type !== 'designed') return;
        const currentProps = currentBlock.props as DesignedProps;
        const newTexts = { ...(currentProps.texts ?? {}), [data.id]: data.text };
        editorStore.updateBlockProps(currentBlock.id, { texts: newTexts });
        const after = editorStore.getState().site?.blocks[0];
        lastWrittenPropsRef.current = JSON.stringify(after ? after.props : { ...currentProps, texts: newTexts });
      } else if (data.type === 'scroll') {
        if (typeof data.y === 'number' && Number.isFinite(data.y)) scrollYRef.current = data.y;
      } else if (data.type === 'ready') {
        if (scrollYRef.current > 0 && iframe.contentWindow) {
          iframe.contentWindow.postMessage({ target: 'sarnia-design', type: 'scrollTo', y: scrollYRef.current }, '*');
        }
        const state = editorStore.getState();
        const currentBlock = state.site?.blocks[0];
        if (!currentBlock || currentBlock.type !== 'designed') return;
        const currentProps = currentBlock.props as DesignedProps;
        if (iframe.contentWindow) {
          iframe.contentWindow.postMessage(
            { target: 'sarnia-design', type: 'vars', vars: currentProps.vars ?? {} },
            '*',
          );
        }
      } else if (data.type === 'image' && typeof data.id === 'string') {
        setSelection((prev) => ({
          kind: 'image',
          id: data.id!,
          nonce: (prev?.nonce ?? 0) + 1,
        }));
        setActiveTab('images');
      } else if (data.type === 'item' && typeof data.name === 'string' && /^[A-Za-z_$][\w$]{0,60}$/.test(data.name) && Number.isInteger(data.index) && (data.index as number) >= 0 && (data.index as number) < 200) {
        const itemId = `${data.name}:${data.index}`;
        setSelection((prev) => ({ kind: 'item', id: itemId, nonce: (prev?.nonce ?? 0) + 1 }));
        setActiveTab('items');
      } else if (data.type === 'link' && typeof data.id === 'string') {
        setSelection((prev) => ({
          kind: 'link',
          id: data.id!,
          nonce: (prev?.nonce ?? 0) + 1,
        }));
        setActiveTab('links');
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  const update = useCallback((patch: Partial<DesignedProps>) => {
    const state = editorStore.getState();
    const currentBlock = state.site?.blocks[0];
    if (!currentBlock || currentBlock.type !== 'designed') return;
    editorStore.updateBlockProps(currentBlock.id, patch);
    const updatedBlock = editorStore.getState().site?.blocks[0];
    if (updatedBlock && updatedBlock.type === 'designed') {
      lastWrittenPropsRef.current = JSON.stringify(updatedBlock.props);
    } else {
      lastWrittenPropsRef.current = JSON.stringify({ ...(currentBlock.props as DesignedProps), ...patch });
    }
  }, []);

  const post = useCallback((msg: Record<string, unknown>) => {
    if (iframeRef.current?.contentWindow) {
      iframeRef.current.contentWindow.postMessage(
        { target: 'sarnia-design', ...msg },
        '*',
      );
    }
  }, []);

  const assetUrl = useCallback(
    (id: string) => allAssetUrls[id] ?? null,
    [allAssetUrls],
  );

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  const ctx: PanelCtx = useMemo(
    () => ({
      props: props ?? fallbackPanelProps,
      entry: entry ?? {
        id: '',
        title: '',
        description: '',
        textCount: 0,
        vars: [],
        images: [],
        sections: [],
        links: [],
      },
      update,
      post,
      assetUrl,
      reload,
      selection,
    }),
    [props, entry, update, post, assetUrl, reload, selection],
  );

  const templateTexts = useMemo(() => (designHtml ? defaultTexts(designHtml) : null), [designHtml]);
  const issues = useMemo(
    () => (templateTexts && props && entry && meta ? runSiteCheck({ props, entry, meta, defaults: templateTexts }) : []),
    [templateTexts, props, entry, meta],
  );
  const urgentCount = issues.filter((issue) => issue.severity === 'high').length;

  if (!block || block.type !== 'designed' || !designId || !entry) {
    return null;
  }

  const deviceWidth = previewDevice === 'phone' ? '390px' : previewDevice === 'tablet' ? '768px' : '100%';

  return (
    <div className="dw-shell">
      <div className="dw-preview-column" data-tour="dw-preview">
        <div className="dw-preview-toolbar">
          <span className="dw-hint">
            {loaderView
              ? 'Loading screen — click any text or logo to edit it'
              : 'Click any text to edit — click images and links to replace them'}
          </span>
          <button
            type="button"
            className="dw-view-toggle"
            aria-pressed={loaderView}
            onClick={() => {
              setSelection(null);
              setLoaderView((v) => !v);
            }}
          >
            {loaderView ? 'Back to the site' : 'Edit loading screen'}
          </button>
          <ThemeToggle />
        </div>
        <div className="dw-device-frame" style={{ width: deviceWidth }}>
          {loading ? <div className="dw-loading">Loading design…</div> : null}
          {error ? <div role="alert" className="dw-error">{error}</div> : null}
          {!loading && !error && previewSrcDoc ? (
            <iframe
              ref={iframeRef}
              key={reloadKey}
              srcDoc={previewSrcDoc}
              sandbox="allow-scripts"
              title="Design preview"
              className="dw-iframe"
            />
          ) : null}
        </div>
      </div>

      <aside className="dw-sidebar">
        <div className="dw-tabs" role="tablist" aria-label="Design customisation">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              role="tab"
              id={`dw-tab-${tab.id}`}
              data-tour={`dw-tab-${tab.id}`}
              aria-selected={activeTab === tab.id}
              aria-controls={`dw-panel-${tab.id}`}
              className={`dw-tab ${activeTab === tab.id ? 'dw-tab--active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <Icon name={tab.icon} size={16} />
              <span>{tab.label}</span>
              {tab.id === 'check' && urgentCount > 0 ? (
                <span className="dw-tab-badge" aria-label={`${urgentCount} to fix`}>
                  {urgentCount}
                </span>
              ) : null}
            </button>
          ))}
        </div>
        <div
          className="dw-panel-container"
          role="tabpanel"
          id={`dw-panel-${activeTab}`}
          aria-labelledby={`dw-tab-${activeTab}`}
        >
          {activeTab === 'content' && <ContentPanel ctx={ctx} />}
          {activeTab === 'images' && <ImagesPanel ctx={ctx} />}
          {activeTab === 'links' && <LinksPanel ctx={ctx} />}
          {activeTab === 'sections' && <SectionsPanel ctx={ctx} />}
          {activeTab === 'items' && <ItemsPanel ctx={ctx} />}
          {activeTab === 'effects' && <EffectsPanel ctx={ctx} />}
          {activeTab === 'colours' && <ColoursPanel ctx={ctx} />}
          {activeTab === 'fonts' && <FontsPanel ctx={ctx} />}
          {activeTab === 'business' && <BusinessPanel ctx={ctx} defaults={templateTexts} />}
          {activeTab === 'check' &&<CheckPanel ctx={ctx} issues={issues} onGoTo={setActiveTab} />}
          {activeTab === 'publish' && (
            <>
              {templateTexts ? <CheckSummary issues={issues} onReview={() => setActiveTab('check')} /> : null}
              <PublishDialog />
            </>
          )}
        </div>
      </aside>
    </div>
  );
}
